import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { DvlaEndorsement, DvlaEntitlement } from "@/types/compliance"
import { Person } from "@/types/person"

export type EligibilityStatus = "Eligible" | "Ineligible" | "Pending" | "Failed"

export interface ComplianceRecord {
  id: string
  person_id: string
  first_name: string | null
  surname: string | null
  person: string | null
  location: string[] | null
  status: string | null
  depot?: string | null
  contractor_name?: string
  contractor_initials?: string
  check_date: string | null
  points: number
  endorsements: string[]
  eligibility: EligibilityStatus
  licence_status: string | null
  licence_number: string | null
  check_status?: string | null
}

export interface PersonCompliance extends Person {
  driver_licence_records: {
    id: string
    driving_licence_number: string | null
    licence_status: string | null
    licence_type: string | null
    entitlement: DvlaEntitlement[] | null
    endorsements: DvlaEndorsement[] | null
    created_at: string | null
    points?: number
  }[]
  // Add compliance-specific fields
  eligibility?: EligibilityStatus
  depot?: string | null
  contractor_name?: string
  contractor_initials?: string
}

export interface ComplianceSummary {
  all: number
  eligible: number
  ineligible: number
  failed: number
  pending: number
}

interface UseComplianceRecordsOptions {
  limit?: number
  offset?: number
  searchQuery?: string
  locationFilter?: string
  driverManagerTeamFilter?: string
  sheriffTypeFilter?: string
  dspFilter?: string
  eligibilityFilter?: EligibilityStatus | "all"
  dateFrom?: string
  dateTo?: string
}

export function useComplianceRecords({
  limit = 25,
  offset = 0,
  searchQuery = "",
  locationFilter = "",
  driverManagerTeamFilter = "",
  sheriffTypeFilter = "",
  dspFilter = "",
  eligibilityFilter = "all",
  dateFrom,
  dateTo,
}: UseComplianceRecordsOptions = {}) {
  return useQuery({
    queryKey: [
      "licence-checks",
      searchQuery,
      locationFilter,
      driverManagerTeamFilter,
      sheriffTypeFilter,
      dspFilter,
      eligibilityFilter,
      dateFrom,
      dateTo,
      limit,
      offset,
    ],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("licence-checks", {
        body: {
          searchQuery,
          locationFilter,
          driverManagerTeamFilter,
          sheriffTypeFilter,
          dspFilter,
          eligibilityFilter,
          dateFrom,
          dateTo,
          limit,
          offset,
        },
      })

      if (error) {
        throw new Error(`Failed to fetch compliance records: ${error.message}`)
      }

      if (!data.success) {
        throw new Error(data.error || "Failed to fetch compliance records")
      }

      return {
        records: data.data as ComplianceRecord[],
        summary: data.summary as ComplianceSummary,
        total: data.total as number,
      }
    },
  })
}
